import styled from '@emotion/styled';
import classNames from 'classnames';
import React, { forwardRef, useCallback, useEffect, useId, useRef } from 'react';
import { theme } from 'styles/theme/theme';

export interface UDCompactCheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'children'> {
  /** Подпись справа от галки, в том же `<label>`: клик по тексту переключает. Без подписи — голый флажок. */
  label?: React.ReactNode;
  /**
   * Смешанное состояние («у части выбранных включено»): рисуется «–». У атрибута такого нет — это DOM-свойство,
   * поэтому оно ставится в эффекте (MDN: input checkbox, «Indeterminate state checkboxes»).
   */
  indeterminate?: boolean;
  /** Класс строки с подписью (или самого флажка без подписи). Шрифт и цвет подписи наследуются от родителя. */
  className?: string;
}

/**
 * Маленький нативный флажок для плотных панелей (инспектор редактора): видимый `<input type="checkbox">` в цвете
 * акцента, а не скрытый input с нарисованным квадратом, как у `UDCheckbox`.
 *
 * Не кладите его в свой `<label>`: подпись уже внутри, и вложенный label переключал бы флажок дважды.
 */
const UDCompactCheckbox = forwardRef<HTMLInputElement, UDCompactCheckboxProps>(function UDCompactCheckbox(props, ref) {
  const { label, indeterminate = false, className, id: idProp, ...inputProps } = props;
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const innerRef = useRef<HTMLInputElement | null>(null);

  const setRefs = useCallback(
    (node: HTMLInputElement | null) => {
      innerRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );

  useEffect(() => {
    if (innerRef.current) innerRef.current.indeterminate = indeterminate;
  }, [indeterminate, inputProps.checked]);

  const input = (
    <Input {...inputProps} id={id} ref={setRefs} type="checkbox" className={label === undefined ? className : undefined} />
  );
  if (label === undefined) return input;

  return (
    <Row htmlFor={id} className={classNames(className, inputProps.disabled && 'is-disabled')}>
      {input}
      <span>{label}</span>
    </Row>
  );
});

export default UDCompactCheckbox;

// Специфичность одного класса: экран со своим акцентом (редактор медиа-менеджера) перекрашивает флажок своим правилом.
const Input = styled.input`
  margin: 0;
  accent-color: ${theme.colors.AccentNaplesYellow};
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
`;

const Row = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;

  /* Выключенный пункт гаснет целиком, вместе с подписью: своя прозрачность у вызывающего не нужна. Два класса
     специфичнее цвета, который строке задаёт обёртка вроде styled(UDCompactCheckbox). */
  &.is-disabled {
    color: ${theme.colors.InkDisabled};
    cursor: not-allowed;
  }
`;
